'use client';

import { useState } from 'react';
import Link from 'next/link';
import { AlertCircle, ArrowRight, CheckCircle2, Info, Loader2, XCircle } from 'lucide-react';

type Severity = 'critical' | 'warning' | 'info' | 'pass';

interface Finding {
  severity: Severity;
  code: string;
  title: string;
  detail: string;
  fix?: string;
}

interface ToolResponse {
  domain: string;
  cached: boolean;
  checked_at: string;
  grade?: string;
  score?: number;
  findings?: Finding[];
  [key: string]: unknown;
}

const SEVERITY_STYLES: Record<Severity, { icon: typeof CheckCircle2; className: string; label: string }> = {
  critical: { icon: XCircle, className: 'text-red-600 dark:text-red-400', label: 'Critical' },
  warning: { icon: AlertCircle, className: 'text-amber-600 dark:text-amber-400', label: 'Warning' },
  info: { icon: Info, className: 'text-blue-600 dark:text-blue-400', label: 'Note' },
  pass: { icon: CheckCircle2, className: 'text-emerald-600 dark:text-emerald-400', label: 'Pass' },
};

const GRADE_STYLES: Record<string, string> = {
  A: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  B: 'bg-lime-500/10 text-lime-600 dark:text-lime-400',
  C: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  D: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
  F: 'bg-red-500/10 text-red-600 dark:text-red-400',
};

export interface DomainToolProps {
  /** Path under /api/tools, e.g. 'spf'. */
  endpoint: string;
  placeholder?: string;
  /** Rendered under the result when the tool has no findings list. */
  renderRaw?: boolean;
}

export function DomainTool({ endpoint, placeholder = 'yourdomain.com', renderRaw }: DomainToolProps) {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ToolResponse | null>(null);

  async function run(e: React.FormEvent) {
    e.preventDefault();
    if (!domain.trim() || loading) return;

    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(`/api/tools/${endpoint}?domain=${encodeURIComponent(domain.trim())}`);
      const body = await res.json();
      if (!body.success) {
        setError(body.error?.message ?? 'Something went wrong.');
      } else {
        setResult(body.data as ToolResponse);
      }
    } catch {
      setError('Could not reach the server. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  const findings = result?.findings ?? [];

  return (
    <div>
      <form onSubmit={run} className="flex flex-col gap-3 sm:flex-row">
        <input
          type="text"
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder={placeholder}
          aria-label="Domain name"
          className="flex-1 rounded-lg border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary"
        />
        <button
          type="submit"
          disabled={loading || !domain.trim()}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {loading ? 'Checking…' : 'Check'}
        </button>
      </form>

      {error && (
        <p role="alert" className="mt-4 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      {result && (
        <div className="mt-8">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-lg font-semibold">{result.domain}</h2>
            {result.grade && (
              <span className={`rounded-lg px-3 py-1 text-sm font-semibold ${GRADE_STYLES[result.grade] ?? ''}`}>
                Grade {result.grade}
                {typeof result.score === 'number' ? ` · ${result.score}/100` : ''}
              </span>
            )}
            {result.cached && (
              <span className="text-xs text-muted-foreground">
                Cached result from {new Date(result.checked_at).toLocaleString()}
              </span>
            )}
          </div>

          {findings.length > 0 && (
            <ul className="mt-6 space-y-3">
              {findings.map((finding) => {
                const style = SEVERITY_STYLES[finding.severity] ?? SEVERITY_STYLES.info;
                const Icon = style.icon;
                return (
                  <li key={finding.code} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-start gap-3">
                      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.className}`} aria-hidden />
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          {finding.title}
                          <span className="sr-only"> — {style.label}</span>
                        </p>
                        <p className="mt-1 break-words text-sm text-muted-foreground">{finding.detail}</p>
                        {finding.fix && (
                          <p className="mt-2 rounded-lg bg-muted px-3 py-2 text-xs">
                            <span className="font-medium">How to fix: </span>
                            {finding.fix}
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {(renderRaw || findings.length === 0) && (
            <pre className="mt-6 overflow-x-auto rounded-xl border border-border bg-muted p-4 text-xs">
              {JSON.stringify(
                Object.fromEntries(
                  Object.entries(result).filter(([k]) => !['domain', 'cached', 'checked_at', 'findings'].includes(k)),
                ),
                null,
                2,
              )}
            </pre>
          )}

          <div className="mt-8 rounded-xl border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">
              Peakflow runs these checks against every sending mailbox on a schedule, warms new
              mailboxes before they send, and pauses a campaign when reputation slips.
            </p>
            <Link
              href="/demo"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary"
            >
              See how it works
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
